import React, { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase_client';
import { useSession } from '../contexts/SessionContext';

export interface CreatedCompany {
  company_id: string;
  company_name: string;
  address: string;
}

interface CreateOrganizationModalProps {
  open: boolean;
  defaultCountry?: string;
  onClose: () => void;
  onCreated: (company: CreatedCompany) => void;
}

export const OPEN_CREATE_ORG_MODAL_EVENT = 'open-create-org-modal';

export default function CreateOrganizationModal({
  open,
  defaultCountry,
  onClose,
  onCreated,
}: CreateOrganizationModalProps) {
  const { user, refresh, activeOrg } = useSession();
  const isOwner = activeOrg?.roleId === 1;
  const [organizationName, setOrganizationName] = useState('');
  const [address, setAddress] = useState('');
  const [country, setCountry] = useState('MY');
  const [countries, setCountries] = useState<
    { iso: string; name: string; country_phone_code: string }[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const fetchCountries = useCallback(async () => {
    const { data, error } = await supabase
      .from('countries')
      .select('iso, name, country_phone_code')
      .order('name', { ascending: true });

    if (error) {
      console.error('CreateOrganizationModal: countries', error);
      return;
    }
    setCountries(data || []);
  }, []);

  useEffect(() => {
    if (open) {
      fetchCountries();
      setMsg(null);
    }
  }, [open, fetchCountries]);

  useEffect(() => {
    if (open) {
      setCountry(defaultCountry || 'MY');
    } else {
      setOrganizationName('');
      setAddress('');
      setCountry('MY');
      setMsg(null);
      setLoading(false);
    }
  }, [open, defaultCountry]);

  const handleSubmit = async (e: React.FormEvent) => {
    if (!isOwner) {
        setMsg('Only owner can create a new organization.');
        return;
    }

    e.preventDefault();
    setMsg(null);

    const name = organizationName.trim();
    const addr = address.trim();
    if (!name || !addr) {
      setMsg('Please fill in company name and address.');
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('create_organization_for_current_user', {
        p_name: name,
        p_address: addr,
        p_country_iso: country,
      });

      if (error) {
        setMsg(error.message);
        return;
      }

      if (!data?.success) {
        setMsg((data?.error as string) || 'Failed to create organization');
        return;
      }

      const orgId = data.org_id as string;
      await refresh();
      onCreated({
        company_id: orgId,
        company_name: name,
        address: addr,
      });
      onClose();
    } catch (err: unknown) {
      setMsg(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  const email = user?.email ?? '';

  return (
    <div
      className="fixed inset-0 z-[10050] flex items-center justify-center p-4 bg-black/50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-org-modal-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <h2 id="create-org-modal-title" className="text-xl font-semibold text-gray-900">
            Create Organization
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg text-gray-500 hover:bg-gray-100"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {!isOwner && (
            <div className="text-sm p-3 rounded-lg bg-amber-50 text-amber-800 border border-amber-200">
                Only owner can create a new organization. Please contact your owner to create a new organization for you.
            </div>
            )}
          <div>
            <label className="block text-gray-700 mb-1 font-medium">Email</label>
            <input
              type="email"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 bg-gray-50 text-gray-700 cursor-not-allowed"
              value={email}
              readOnly
              disabled
            />
          </div>

          <div>
            <label className="block text-gray-700 mb-1 font-medium">Company Name</label>
            <input
              type="text"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
              value={organizationName}
              onChange={(e) => setOrganizationName(e.target.value)}
              placeholder="e.g., ABC Company Sdn Bhd"
              disabled={!isOwner || loading}
              required
            />
          </div>

          <div>
            <label className="block text-gray-700 mb-1 font-medium">Address</label>
            <textarea
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
              value={address}
              rows={4}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Enter organization address"
              disabled={!isOwner || loading}
              required
            />
          </div>

          <div>
            <label className="block text-gray-700 mb-1 font-medium">Filing Country</label>
            <select
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              disabled={!isOwner || loading}
              required
            >
              {countries.map((c) => (
                <option key={c.iso} value={c.iso}>
                  {c.name}
                  {c.iso === 'MY' ? ' (MBRS)' : c.iso === 'SG' ? ' (ACRA BizFinx)' : ''}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
            This determines which country's XBRL your organization will work for.
            </p>
          </div>

          {msg && (
            <div className="text-sm p-3 rounded-lg bg-red-50 text-red-800">{msg}</div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="f-btn f-btn-soft" disabled={loading}>
              Cancel
            </button>
            <button
                type="submit"
                className="f-btn f-btn-primary"
                disabled={loading || !isOwner}
                title={!isOwner ? 'Only owner can create a new organization' : undefined}
                >
                {loading ? 'Creating…' : !isOwner ? 'Owner Only' : 'Create Organization'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}